import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { odStatusFilesTypeValues } from '@Common/types/bo/b2b/status_files';
import { zodFundinfoPricingFrequency } from '@Common/types/bo/instruments';
import { OrderInitiatorValues } from '@Common/types/bo/orders/common';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import {
  ZodTransactionOrderCancellationInitiator,
  ZodTransactionOrderCancellationReason,
} from '@Common/types/profile/transactions/enums';
import { ZodId } from '@Common/types/request';
import { ZodCurrencySchema } from '@Common/types/zod/zod-amount';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateString, ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { ZodJSONObject } from '@Common/types/zod/zod-json';
import { createZodNumberLike } from '@Common/types/zod/zod-number-like';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const B2bOrderSides = ['buy', 'sell'] as const;
export const ZodOdOrderSide = z.enum(B2bOrderSides);
export const B2bOrderOperations = [
  'subscription',
  'arbitrage',
  'redemption',
  'liquidation',
] as const;
const ZodOdOperation = z.enum(B2bOrderOperations);
export const B2bOrderStatusValues = [
  'pending',
  'filled',
  'shares_canceled',
  'shares_new',
  'pending_funds',
  'shares_pending_arbitrage',
  'shares_pending_approval',
  'shares_pending_company',
  'shares_pending_campaign',
  'rejected',
  'shares_pending_reallocation',
  'shares_pending_cutoff_period',
  'shares_pending_blocked_instrument',
  'shares_pending_triggered_order',
  'shares_pending_scheduling',
] as const;
export const ZodOdOrderStatus = z.enum(B2bOrderStatusValues);
export type OdOrderStatus = (typeof B2bOrderStatusValues)[number];

export const B2bOrderOddoStatus = ['sent', 'acknowledged', 'confirmed', 'rejected'] as const;
const ZodB2bOrderOddoStatus = z.enum(B2bOrderOddoStatus);

export const FULLY_MANAGED_PLAN_CONTRIBUTION_ORDER_ID_PREFIX =
  'fully_managed_plan_contribution_' as const;

export const ZodB2bFullyManagedPlanContributionOrderId = z
  .string()
  .regex(/^fully_managed_plan_contribution_\d+$/);

export type B2bFullyManagedPlanContributionOrderId = z.infer<
  typeof ZodB2bFullyManagedPlanContributionOrderId
>;

export const ZodB2bOrdersListId = z.union([ZodId, ZodB2bFullyManagedPlanContributionOrderId]);
export type B2bOrdersListId = z.infer<typeof ZodB2bOrdersListId>;

export function isFullyManagedPlanContributionOrderId(
  id: B2bOrdersListId,
): id is B2bFullyManagedPlanContributionOrderId {
  return typeof id === 'string' && id.startsWith(FULLY_MANAGED_PLAN_CONTRIBUTION_ORDER_ID_PREFIX);
}

export function parseFullyManagedPlanContributionOrderId(
  id: B2bFullyManagedPlanContributionOrderId,
): number {
  return Number(id.slice(FULLY_MANAGED_PLAN_CONTRIBUTION_ORDER_ID_PREFIX.length));
}

const ZodGetB2bOrdersOneResponse = z.object({
  id: ZodB2bOrdersListId,
  company_id: ZodId,
  contract_id: ZodId,
  employee_id: ZodId.nullable(),
  user_id: ZodId,
  instrument_id: ZodId.nullable(),
  isin: z.string().trim().nullable(),
  side: ZodOdOrderSide,
  quantity: z.number().nullable(),
  short_name: ZodTrimmedString.nullable(),
  operation: ZodOdOperation,
  user_currency: ZodCurrencySchema,
  instrument_currency: ZodCurrencySchema,
  status: ZodOdOrderStatus,
  created_at: z.date(),
  updated_at: z.date(),
  total_gross_amount: z.number().int().nullable(),
  user_currency_amount: z.number().int().nullable(),
  user_currency_amount_input: z.number().int().nullable(),
  net_amount: z.number().int().nullable(),
  filled_quantity: z.number().nullable(),
  b2b_company_plan_id: ZodId,
  od_orders_aggregated_id: ZodId.nullable(),
  linked_transfer_id: ZodTrimmedString.nullable(),
  is_matching_contribution: z.boolean(),
  matching_contribution_pool_year: z.number().int().nullable(),
  tax_amount: z.number().int(),
  tax_info: ZodJSONObject.nullable(),
  fee_amount: z.number().int(),
  entry_fee_amount: z.number().int(),
  entry_fee_company_amount: z.number().int(),
  entry_fee_employee_amount: z.number().int(),
  entry_fee_rate: z.number(),
  shares_fee_amount: z.number().int(),
  fee_info: ZodJSONObject.nullable(),
  matched_contribution_order_id: ZodId.nullable(),
  matching_contribution_order_id: ZodId.nullable(),
  savings_funds_requests_id: z.number().nullable(),
  filled_avg_price: z.number().nullable(),
  campaign_id: z.number().nullable(),
  oddo_status: ZodB2bOrderOddoStatus.nullable(),
  correlation_id: ZodTrimmedString.nullable(),
  initiator: z.enum(OrderInitiatorValues).nullable(),
  recurring_fee_charge_id: ZodId.nullable(),
  reference_uuid: z.string().uuid().nullable(),
  b2b_withdrawal_id: ZodId.nullable(),
  arbitrage_sell_order_id: ZodId.nullable(),
  trade_confirmation_document_id: ZodTrimmedString.nullable(),
  b2b_transfer_request_id: ZodId.nullable(),
  passive_price_reference_at: z.date().nullable(),
  passive_nav_date_at: ZodDateString.nullable(),
  rejected_code: ZodTrimmedString.nullable(),
  rejected_description: ZodTrimmedString.nullable(),
  passive_nav_source: ZodTrimmedString.nullable(),
  filled_at: z.date().nullable(),
  fully_managed_plan_contribution_id: ZodId.nullable(),
  is_managed: z.boolean(),
});

export const ZodOdStatusFilesType = z.enum(odStatusFilesTypeValues);

export type GetB2bOrdersOneResponse = z.TypeOf<typeof ZodGetB2bOrdersOneResponse>;

export const ZodGetB2bOrdersResponse = ZodGetB2bOrdersOneResponse.array();
export type GetB2bOrdersResponse = z.TypeOf<typeof ZodGetB2bOrdersResponse>;

export const getB2bOrdersSortColumns: (keyof GetB2bOrdersOneResponse)[] = [
  'company_id',
  'contract_id',
  'employee_id',
  'user_id',
  'instrument_id',
  'side',
  'quantity',
  'short_name',
  'operation',
  'user_currency',
  'status',
  'created_at',
  'updated_at',
  'user_currency_amount',
  'user_currency_amount_input',
  'filled_quantity',
  'b2b_company_plan_id',
  'od_orders_aggregated_id',
  'linked_transfer_id',
  'is_matching_contribution',
  'matching_contribution_pool_year',
  'tax_amount',
  'matched_contribution_order_id',
  'campaign_id',
  'oddo_status',
  'correlation_id',
  'b2b_withdrawal_id',
  'arbitrage_sell_order_id',
  'b2b_transfer_request_id',
];

const ZodFilters = z
  .object({
    ...ZodGetB2bOrdersOneResponse.pick({
      quantity: true,
      user_currency: true,
      filled_quantity: true,
      is_managed: true,
      is_matching_contribution: true,
      tax_amount: true,
      filled_avg_price: true,
      reference_uuid: true,
    }).shape,
    id: createZodArrayable(ZodB2bOrdersListId),
    company_id: createZodArrayable(z.number()),
    employee_id: createZodArrayable(z.number()),
    user_id: createZodArrayable(z.number()),
    instrument_id: createZodArrayable(z.number()),
    side: createZodArrayable(ZodOdOrderSide),
    short_name: createZodArrayable(ZodTrimmedString),
    operation: createZodArrayable(ZodOdOperation),
    status: createZodArrayable(ZodOdOrderStatus),
    b2b_company_plan_id: createZodArrayable(z.number()),
    od_orders_aggregated_id: createZodArrayable(z.number()),
    linked_transfer_id: createZodArrayable(ZodTrimmedString),
    matched_contribution_order_id: createZodArrayable(z.number()),
    matching_contribution_pool_year: createZodArrayable(z.number().int()),
    savings_funds_requests_id: createZodArrayable(z.number()),
    campaign_id: createZodArrayable(z.number()),
    oddo_status: createZodArrayable(ZodB2bOrderOddoStatus),
    correlation_id: createZodArrayable(ZodTrimmedString),
    initiator: createZodArrayable(z.enum(OrderInitiatorValues)),
    recurring_fee_charge_id: createZodArrayable(z.number()),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    user_currency_amount: createZodNumberLike(z.number()),
    user_currency_amount_input: createZodNumberLike(z.number()),
    b2b_withdrawal_id: createZodArrayable(z.number()),
    arbitrage_sell_order_id: createZodArrayable(z.number()),
    contract_id: createZodArrayable(z.number()),
    b2b_transfer_request_id: createZodArrayable(z.number()),
    fully_managed_plan_contribution_id: createZodArrayable(z.number()),
  })
  .partial();

export const ZodGetB2bOrdersQuery = buildZodBoListQueryParsed(ZodFilters, getB2bOrdersSortColumns, {
  range: [0, 10000],
});

export type GetB2bOrdersQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bOrdersSortColumns)[number]
>;

const ZodCorrectionOrder = z.object({
  id: ZodId,
  status: ZodOdOrderStatus,
  side: ZodOdOrderSide,
  created_at: ZodDateTimeString,
  filled_at: ZodDateTimeString.nullable(),
  user_currency_amount: z.number().int(),
  gross_contribution_user_currency_amount: z.number().int(),
  employer_fund_request_contribution_tax_amount: z.number().int(),
  employee_post_contribution_tax_amount: z.number().int(),
  tax_info: ZodJSONObject.nullable(),
  savings_funds_requests_id: z.number().int().nullable(),
  filled_avg_price: z.number().nullable(),
  executed_avg_price: z.number().nullable(),
  executed_amount: z.number().nullable(),
  executed_quantity: z.number().nullable(),
  fee_amount: z.number().int(),
});

export type CorrectionOrder = z.TypeOf<typeof ZodCorrectionOrder>;

export const LedgerCorrectionTypeValues = [
  'es_od_entry_fee_correction',
  'es_od_fr_contribution_tax_amount_correction',
  'es_od_user_currency_amount_correction',
] as const;

// A row of es_od_orders_corrections made for the order: a delta on one of its amounts, with or
// without a correction order behind it. An arbitrage sell leg sits on the arbitrage buy
// (applied_on_od_order_id) and is linked back to the order it corrects.
const ZodLedgerCorrection = z.object({
  id: ZodId,
  type: z.enum(LedgerCorrectionTypeValues),
  amount: z.number().int(),
  created_at: ZodDateTimeString,
  applied_on_od_order_id: ZodId,
  correction_od_order_id: ZodId.nullable(),
  correction_od_order_status: ZodOdOrderStatus.nullable(),
  b2b_correction_id: ZodId.nullable(),
  b2b_campaign_retractation_id: ZodId.nullable(),
  retractation_b2b_campaign_id: ZodId.nullable(),
  // Left out of the order's effective state: its payment failed or its correction order was voided
  is_voided: z.boolean(),
});

export type LedgerCorrection = z.TypeOf<typeof ZodLedgerCorrection>;

// The order as it stands once its corrections are folded in, by the fold correction orders are
// placed from (corrected_order_state.ts)
const ZodB2bOrderEffectiveState = z.object({
  user_currency_amount_input: z.number().int(),
  // Null when a legacy arbitrage sell leg never recorded what it took off the order
  filled_quantity: z.number().nullable(),
  entry_fee_employee_amount: z.number().int(),
  employer_fund_request_contribution_tax_amount: z.number().int(),
});

export type B2bOrderEffectiveState = z.TypeOf<typeof ZodB2bOrderEffectiveState>;

export const ZodB2bOrderPassiveNavTraceStep = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('cutoff'),
    cutoff: z.enum(['nav', 'admin']),
    cutoff_time: ZodTrimmedString,
    missed_cutoff: z.boolean(),
    date: z.date(),
  }),
  z.object({
    type: z.literal('cutoff_bypassed'),
    date: z.date(),
  }),
  z.object({
    type: z.literal('execution_delay'),
    delay_days: z.number().int(),
    day_number: z.number().int(),
    date: z.date(),
  }),
  z.object({
    type: z.literal('weekend'),
    weekend_day: z.enum(['saturday', 'sunday']),
    date: z.date(),
  }),
  z.object({
    type: z.literal('market_holiday'),
    country_codes: z.string().array(),
    date: z.date(),
  }),
]);
export type B2bOrderPassiveNavTraceStep = z.TypeOf<typeof ZodB2bOrderPassiveNavTraceStep>;

export const ZodB2bOrderPassiveNavCalculation = z.object({
  nav_cut_off: ZodTrimmedString.nullable(),
  admin_cut_off: ZodTrimmedString.nullable(),
  nav_execution_delay: z.number().int().nullable(),
  pricing_frequency: zodFundinfoPricingFrequency.nullable(),
  fund_calendar_country_codes: z.string().array().nullable(),
  trace: ZodB2bOrderPassiveNavTraceStep.array().nullable(),
  passive_nav_date: z.date().nullable(),
});
export type B2bOrderPassiveNavCalculation = z.TypeOf<typeof ZodB2bOrderPassiveNavCalculation>;

export const ZodGetB2bOrderDetailsResponse = z.object({
  ...ZodGetB2bOrdersOneResponse.omit({
    id: true,
    instrument_id: true,
    reference_uuid: true,
    fully_managed_plan_contribution_id: true,
    is_managed: true,
  }).shape,
  id: ZodId,
  instrument_id: ZodId,
  reference_uuid: z.string().uuid(),
  files: z.array(z.object({ url: ZodTrimmedString, type: ZodOdStatusFilesType })),
  is_cancelable: z.boolean(),
  qty_decimal_places: z.number().int(),
  corrected_od_order_id: ZodId.nullable(),
  cancellation_reason: ZodTransactionOrderCancellationReason.nullable(),
  cancellation_initiator: ZodTransactionOrderCancellationInitiator.nullable(),
  canceled_at: z.date().nullable(),
  b2b_recurring_investment_id: ZodId.nullable(),
  b2b_individual_operation_id: ZodId.nullable(),
  correction_orders: z.array(ZodCorrectionOrder).default([]),
  ledger_corrections: z.array(ZodLedgerCorrection).default([]),
  effective_state: ZodB2bOrderEffectiveState.nullable(),
  internal_note: ZodJSONObject.nullable(),
  passive_nav_calculation: ZodB2bOrderPassiveNavCalculation.nullable(),
});
export type GetB2bOrderDetailsResponse = z.TypeOf<typeof ZodGetB2bOrderDetailsResponse>;

export const B2bFullyManagedPlanContributionsStatusValue = [
  'pending',
  'paid',
  'funding_failed',
  'shares_canceled',
] as const;

export const ZodB2bFullyManagedPlanContributionsStatus = z.enum(
  B2bFullyManagedPlanContributionsStatusValue,
);

export type B2bFullyManagedPlanContributionsStatus = z.infer<
  typeof ZodB2bFullyManagedPlanContributionsStatus
>;

export const B2bFullyManagedPlanContributionsTypeValue = [
  'voluntary_deposit',
  'employer_matching_contribution',
  'campaign_allocation',
  'cross_plan_arbitrage',
] as const;

export const ZodB2bFullyManagedPlanContributionsType = z.enum(
  B2bFullyManagedPlanContributionsTypeValue,
);

export type B2bFullyManagedPlanContributionsType = z.infer<
  typeof ZodB2bFullyManagedPlanContributionsType
>;

export const ZodGetB2bFullyManagedPlanContributionsOneResponse = z.object({
  id: z.number().int(),
  user_id: z.number().int(),
  b2b_company_id: z.number().int(),
  status: ZodB2bFullyManagedPlanContributionsStatus,
  contribution_type: ZodB2bFullyManagedPlanContributionsType,
  user_currency: ZodCurrency,
  paid_at: z.date().nullable(),
  b2b_campaign_id: z.number().int().nullable(),
  voluntary_deposit_fully_managed_plan_contribution_id: z.number().int().nullable(),
  b2b_fully_managed_allocation_id: z.number().int(),
  created_at: z.date(),
  updated_at: z.date(),
  total_gross_amount: z.number().int(),
  user_currency_amount: z.number().int(),
  fee_amount: z.number().int(),
  tax_amount: z.number().int(),
  tax_info: ZodJSONObject.nullable(),
  b2b_company_plan_id: z.number().int(),
  savings_funds_request_id: z.number().int().nullable(),
  matching_contribution_id: z.number().int().nullable(),
});

export type GetB2bFullyManagedPlanContributionsOneResponse = z.TypeOf<
  typeof ZodGetB2bFullyManagedPlanContributionsOneResponse
>;

export const ZodGetB2bFullyManagedPlanContributionsResponse =
  ZodGetB2bFullyManagedPlanContributionsOneResponse.array();

export type GetB2bFullyManagedPlanContributionsResponse = z.infer<
  typeof ZodGetB2bFullyManagedPlanContributionsResponse
>;

export const getB2bFullyMangedPlanContributionsSortColumns: (keyof GetB2bFullyManagedPlanContributionsOneResponse)[] =
  [
    'id',
    'created_at',
    'updated_at',
    'status',
    'savings_funds_request_id',
    'total_gross_amount',
    'user_currency_amount',
    'tax_amount',
  ];

const ZodGetB2bFullyManagedPlanContributionsFilters = z
  .object({
    id: createZodArrayable(ZodNumberQuery),
    user_id: createZodArrayable(ZodNumberQuery),
    b2b_company_id: createZodArrayable(ZodNumberQuery),
    b2b_campaign_id: createZodArrayable(ZodNumberQuery),
    savings_funds_request_id: createZodArrayable(ZodNumberQuery),
    voluntary_deposit_fully_managed_plan_contribution_id: createZodArrayable(ZodNumberQuery),
    b2b_fully_managed_allocation_id: createZodArrayable(ZodNumberQuery),
    b2b_company_plan_id: createZodArrayable(ZodNumberQuery),
    b2b_employee_id: createZodArrayable(ZodNumberQuery),
    matching_contribution_id: createZodArrayable(ZodNumberQuery),
    status: createZodArrayable(ZodB2bFullyManagedPlanContributionsStatus),
    contribution_type: createZodArrayable(ZodB2bFullyManagedPlanContributionsType),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
  })
  .partial();

export const ZodGetB2bFullyManagedPlanContributionsQuery = buildZodBoListQueryParsed(
  ZodGetB2bFullyManagedPlanContributionsFilters,
  getB2bFullyMangedPlanContributionsSortColumns,
  { range: [0, 10000] },
);

export type GetB2bFullyManagedPlanContributionsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bFullyManagedPlanContributionsFilters>,
  (typeof getB2bFullyMangedPlanContributionsSortColumns)[number]
>;
